import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from '@/react-app/App';
// Enable Tailwind + Design System styles across the app
import '@/react-app/index.css';
// Service Worker para cache inteligente e notificação de updates
import { registerServiceWorker } from '@/lib/sw-manager';

// DEPLOY MARKER: DIAGNOSTICO QUALIFICACOES
if (typeof window !== 'undefined') {
  const BUILD_TS = new Date().toISOString();
  console.info('[DEPLOY_MARKER] Build timestamp', BUILD_TS);
  console.info('[DEPLOY_MARKER] Cache bust:', Date.now());
}

// Runtime fetch normalizer: ensure any request to "/api/..." goes to the API origin
// Works both for relative paths ("/api/...") and absolute same-origin URLs
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch.bind(window);
  const API_BASE =
    (import.meta as unknown as { env?: { VITE_API_URL?: string } })?.env?.VITE_API_URL ||
    'https://airtrust-api-production.airtrust.workers.dev/api';
  const DEFAULT_ORIGIN = API_BASE.replace(/\/api$/, '');

  const computeAltOrigin = (origin: string): string | null => {
    try {
      const u = new URL(origin);
      const host = u.hostname;
      const suffix = '.airtrust.workers.dev';
      if (!host.endsWith(suffix)) return null;
      const proto = u.protocol;
      if (host.endsWith('-production' + suffix)) {
        const altHost = host.replace('-production' + suffix, suffix);
        return `${proto}//${altHost}`;
      } else {
        const altHost = host.replace(suffix, '-production' + suffix);
        return `${proto}//${altHost}`;
      }
    } catch {
      return null;
    }
  };

  const ALT_ORIGIN = computeAltOrigin(DEFAULT_ORIGIN);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let triedAlt = false;
    const override =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('API_ORIGIN_OVERRIDE') : null;
    const API_ORIGIN = override || DEFAULT_ORIGIN;

    const tryOnce = async (originToUse: string): Promise<Response> => {
      const asString = typeof input === 'string' ? input : (input as Request).url;
      try {
        const candidate = new URL(asString, window.location.origin);

        // Same-origin "/api/..." → redirect to Worker API origin
        if (candidate.origin === window.location.origin && candidate.pathname.startsWith('/api/')) {
          const redirected = originToUse + candidate.pathname + candidate.search;
          return await originalFetch(redirected, init);
        }

        // Strictly relative "/api/..."
        if (asString.startsWith('/api/')) {
          const redirected = originToUse + asString;
          return await originalFetch(redirected, init);
        }

        // Otherwise default
        return await originalFetch(input as RequestInfo | URL, init);
      } catch {
        // If URL parsing fails, fallback to original
        return await originalFetch(input as RequestInfo | URL, init);
      }
    };

    // First attempt
    const resp = await tryOnce(API_ORIGIN);

    // If 404 or network error-like status, and we have ALT, retry once
    if ((resp.status === 404 || resp.status === 0) && ALT_ORIGIN && !triedAlt) {
      triedAlt = true;
      const altResp = await tryOnce(ALT_ORIGIN);
      if (altResp.ok) {
        try {
          sessionStorage.setItem('API_ORIGIN_OVERRIDE', ALT_ORIGIN);
          console.info('[API] Pinned API_ORIGIN to ALT due to fallback success:', ALT_ORIGIN);
        } catch {
          // ignore storage errors (privacy modes)
        }
        return altResp;
      }
      // If alt also failed, return original resp to preserve semantics
    }

    return resp;
  };
}

// ✅ Registrar Service Worker APENAS em PRODUÇÃO
// Em desenvolvimento, SW pode causar cache indesejado
if (import.meta.env.PROD) {
  console.log('[SW] Registrando Service Worker (PRODUÇÃO)');
  registerServiceWorker().catch((error) => {
    console.warn('[SW] Falha ao registrar:', error);
  });
} else {
  console.log('[DEV] Service Worker DESABILITADO em desenvolvimento');
  // ✅ Limpar SW antigo se existir
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      if (registrations.length > 0) {
        console.warn('[DEV] Removendo Service Workers antigos...');
        registrations.forEach((registration) => registration.unregister());
      }
    });
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
